import React from 'react';
import { Mail, Phone, Leaf, Linkedin, Instagram, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Us', href: '#about' },
    { name: 'Products', href: '#products' },
    { name: 'Our Process', href: '#process' },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Certificates', href: '/certificates', isRoute: true },
    { name: 'Contact', href: '#contact' }
  ];

  const productCategories = [
    'Fresh Fruits',
    'Indian Spices',
    'Turmeric (Organic)',
    'Ginger (Organic)',
    'Buckwheat (Organic)',
    'Large Black Cardamom (Organic)',
    'Coffee Beans Robusta & Arabica (Organic)',
    'Cocoa bean (Organic)',
    'Nutmeg (Organic)',
    'Cardamom - Green (Organic)',
    'Black Pepper (Organic)'
  ];

  const scrollToSection = (e, href, isRoute) => {
    e.preventDefault();
    if (isRoute) {
      window.location.href = href;
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-gradient-to-b from-amber-950 to-amber-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="h-12 w-12 rounded-full overflow-hidden bg-white shadow-lg">
                <img 
                  src={companyInfo.logo} 
                  alt={companyInfo.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold">{companyInfo.name}</h3>
            </div>
            <p className="text-amber-100 mb-6 leading-relaxed">
              Connecting Indian farms to global markets with premium quality agricultural products and reliable service.
            </p>
            
            {/* Social Media Icons */}
            <div className="flex items-center space-x-4 mb-6">
              <a
                href="https://www.linkedin.com/company/cornerstone-exim"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-100 hover:text-white transition-colors duration-200 hover:scale-110 transform"
                aria-label="LinkedIn"
              >
                <Linkedin size={24} />
              </a>
              <a
                href="https://www.instagram.com/cornerstone_exim?igsh=NTJubGpra2V5ZGNv&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-100 hover:text-white transition-colors duration-200 hover:scale-110 transform"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://www.facebook.com/share/1DfMbdEaY6/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-100 hover:text-white transition-colors duration-200 hover:scale-110 transform"
                aria-label="Facebook"
              >
                <Facebook size={24} />
              </a>
            </div>
            
            <div className="flex items-center space-x-2 text-amber-100">
              <Leaf className="text-green-400" size={20} />
              <span className="text-sm">Direct Farm Sourcing</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-amber-100 hover:text-white transition-colors duration-200 flex items-center space-x-2 group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-green-400 transition-all duration-200"></span>
                      <span>{link.name}</span>
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                      className="text-amber-100 hover:text-white transition-colors duration-200 flex items-center space-x-2 group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-green-400 transition-all duration-200"></span>
                      <span>{link.name}</span>
                    </a>
                  )}
                </li>
              ))}
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-amber-100 hover:text-white transition-colors duration-200 flex items-center space-x-2 group"
                >
                  <span className="w-0 group-hover:w-2 h-0.5 bg-green-400 transition-all duration-200"></span>
                  <span>Privacy Policy</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-bold mb-6">Our Products</h4>
            <ul className="space-y-3">
              {productCategories.map((category) => (
                <li key={category}>
                  <span className="text-amber-100 flex items-center space-x-2">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                    <span>{category}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Mail className="text-green-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-sm text-amber-200 mb-1">Email</p>
                  <a href={`mailto:${companyInfo.email}`} className="text-amber-100 hover:text-white transition-colors block">
                    {companyInfo.email}
                  </a>
                  <a href={`mailto:${companyInfo.emailSecondary}`} className="text-amber-100 hover:text-white transition-colors block mt-1">
                    {companyInfo.emailSecondary}
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="text-green-400 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-sm text-amber-200 mb-1">Phone</p>
                  <a href={`tel:${companyInfo.phone}`} className="text-amber-100 hover:text-white transition-colors">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-amber-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-amber-200 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            
            {/* Social Media Icons - Bottom Bar */}
            <div className="flex items-center space-x-4">
              <a
                href="https://www.linkedin.com/company/cornerstone-exim"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-200 hover:text-white transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://www.instagram.com/cornerstone_exim?igsh=NTJubGpra2V5ZGNv&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-200 hover:text-white transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/share/1DfMbdEaY6/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-200 hover:text-white transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-amber-200">
              <span>Spice Board Registered</span>
              <span>•</span>
              <span>APEDA Registered</span>
              <span>•</span>
              <Link to="/privacy-policy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
